import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { CapturedEventsSection } from 'src/components/payments-ops/killswitch/CapturedEventsSection'
import { KillswitchCard } from 'src/components/payments-ops/killswitch/KillswitchCard'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { KillswitchFeature } from 'src/constants/payments-ops/enums'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getKillswitchFlagsError } from 'src/selectors/usBankKillswitch'
import { useKillswitchStates } from 'src/utils/hooks/paymentsOps/useKillswitchStates'

const SWITCHES_DESCRIPTION =
  'Platform-wide switches. Both affect every partner — each change is confirmed with its scope.'
const CAPTURED_EVENTS_ONLY_DESCRIPTION = 'Events captured while a killswitch was on.'

/**
 * Flags are fetched by `PaymentsOpsLayout` (they also drive the banner); this page only reads.
 * The route opens with either of two permissions, so each half renders only with its own: the
 * switch cards need `feature_flags_view` (the flags GET), the captured-events section
 * `us_bank_killswitch_manage` (its list GET, fired from inside the section).
 */
const KillswitchPageComponent = () => {
  const { payments, webhooks } = useKillswitchStates()
  const flagsError = useSelector(getKillswitchFlagsError)
  const canViewFlags = useHasPermission(InternalPermission.FeatureFlagsView)
  const canListCapturedEvents = useHasPermission(InternalPermission.UsBankKillswitchManage)

  return (
    <div
      data-testid="killswitch-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-auto p-6`}
    >
      <PageHeader
        title="US Bank Killswitch"
        description={canViewFlags ? SWITCHES_DESCRIPTION : CAPTURED_EVENTS_ONLY_DESCRIPTION}
      />
      {canViewFlags && flagsError ? (
        <Typography variant="body2" css={tw`text-critical-500`}>
          {flagsError}
        </Typography>
      ) : null}
      {canViewFlags ? (
        <div css={tw`grid gap-4 lg:grid-cols-2`}>
          <KillswitchCard feature={KillswitchFeature.PaymentProcessing} state={payments} />
          <KillswitchCard feature={KillswitchFeature.Webhooks} state={webhooks} />
        </div>
      ) : null}
      {canListCapturedEvents ? <CapturedEventsSection /> : null}
    </div>
  )
}

export const KillswitchPage = memo(KillswitchPageComponent)
